import axios from 'axios';
import { fetchWithOfflineSupport, getAllProductsOffline, searchProductsOffline } from '../../helpers/offlineApi';
import { setLoading, setMessage } from './status';
import { auth, waitForAuthState } from '../../config/firebase';

export const SET_PRODUCTS = 'SET_PRODUCTS';
export const SET_AUTOCOMPLETE_PRODUCTS = 'SET_AUTOCOMPLETE_PRODUCTS';
export const SET_PRODUCT = 'SET_PRODUCT';
export const SET_EQUIVALENCE = 'SET_EQUIVALENCE';
export const CLEAR_PRODUCTS = 'CLEAR_PRODUCTS';

const API_BASE_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';
const ADMIN_PRODUCTS_BATCH_SIZE = 100;

const extractProductList = (responseData) => {
    const payloads = [
        responseData,
        responseData && responseData.data,
        responseData && responseData.result,
        responseData && responseData.results,
        responseData && responseData.data && responseData.data.data,
        responseData && responseData.data && responseData.data.result
    ];

    for (const payload of payloads) {
        if (Array.isArray(payload)) return payload;
        if (payload && Array.isArray(payload.products)) return payload.products;
        if (payload && Array.isArray(payload.results)) return payload.results;
    }

    return [];
};

export const fetchProducts = (filters = {}, preferOffline = false) => async dispatch => {
    dispatch(setLoading(true));

    try {
        if (preferOffline) {
            const offlineProducts = await getAllProductsOffline(filters);
            dispatch({
                type: SET_PRODUCTS,
                payload: offlineProducts
            });
            return offlineProducts;
        }

        try {
            const response = await axios.get(`${API_BASE_URL}/products`, {
                params: {
                    limit: filters.limit || 1000,
                    nom: filters.search || '',
                    laboratoire: filters.laboratoire || '',
                    forme: filters.forme || ''
                },
                timeout: 15000
            });

            const responseData = response.data;
            const productPayloads = [
                responseData,
                responseData && responseData.data,
                responseData && responseData.result,
                responseData && responseData.data && responseData.data.data
            ];
            const products = productPayloads.reduce((found, payload) => {
                if (found.length > 0) return found;
                if (Array.isArray(payload)) return payload;
                return payload && Array.isArray(payload.products) ? payload.products : found;
            }, []);

            if (products.length > 0) {
                const mongoProducts = products.map(product => ({
                    id: product._id || product.id,
                    _id: product._id || product.id,
                    ...product,
                    source: 'mongodb'
                }));


                dispatch({
                    type: SET_PRODUCTS,
                    payload: mongoProducts
                });

                dispatch(setMessage({
                    message: `${mongoProducts.length} produits avec prix chargés`,
                    status: 'success'
                }));

                return mongoProducts;
            } else {

            }
        } catch (mongoError) {

        }

        const offlineProducts = await getAllProductsOffline(filters);
        dispatch({
            type: SET_PRODUCTS,
            payload: offlineProducts
        });

        if (offlineProducts.length > 0) {
            dispatch(setMessage({
                message: 'Données chargées depuis le cache hors ligne',
                status: 'info'
            }));
        }

        return offlineProducts;

    } catch (error) {
        dispatch(setMessage({
            message: 'Impossible de charger les produits',
            status: 'danger'
        }));
        return [];
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchAutocompleteProducts = (query, limit = 20) => async dispatch => {
    if (!query || query.trim().length < 2) {
        dispatch({
            type: SET_AUTOCOMPLETE_PRODUCTS,
            payload: []
        });
        return [];
    }

    try {
        const searchTerm = query.trim().toLowerCase();
        const searchWords = searchTerm.split(/\s+/).filter(w => w.length > 0);

        // Fonction helper pour vérifier si tous les mots sont présents
        const containsAllWords = (text, words) => {
            if (!text) return false;
            const lowerText = text.toLowerCase();
            return words.every(word => lowerText.includes(word));
        };

        let matchedProducts = [];

        try {
            const { getAllProductsOffline } = await import('../../helpers/offlineApi');
            const localProducts = await getAllProductsOffline({
                search: searchTerm,
                limit: limit * 3 // Plus de produits pour mieux filtrer
            });

            if (localProducts && localProducts.length > 0) {
                matchedProducts = localProducts
                    .filter(product => {
                        const nom = product.nom || '';
                        const nomCommercial = product.nomCommercial || '';
                        const dci = product.DCI || product.dci || '';
                        const laboratoire = product.laboratoire || product.fabricant || '';

                        // Support recherche multi-termes
                        return containsAllWords(nom, searchWords) ||
                            containsAllWords(nomCommercial, searchWords) ||
                            containsAllWords(dci, searchWords) ||
                            containsAllWords(laboratoire, searchWords);
                    })
                    .slice(0, limit)
                    .map(product => ({
                        _id: product.id || product._id,
                        nom: product.nom || 'Produit sans nom',
                        nomCommercial: product.nomCommercial || product.nom || '',
                        DCI: product.DCI || product.dci || '',
                        forme: product.forme || product.form || '',
                        dosage: product.dosage || product.dose || '',
                        laboratoire: product.laboratoire || product.fabricant || 'Non spécifié',
                        prix: product.prix || product.price || '0'
                    }));

                if (matchedProducts.length > 0) {
                    dispatch({
                        type: SET_AUTOCOMPLETE_PRODUCTS,
                        payload: matchedProducts
                    });
                    return matchedProducts;
                }
            }
        } catch (localError) {
        }

        try {
            const { auth, firestore } = await import('../../config/firebase');
            const currentUser = auth.currentUser || await waitForAuthState();

            if (currentUser) {
                const productsRef = firestore.collection('products');

                const queries = [
                    productsRef
                        .where('nom', '>=', searchTerm)
                        .where('nom', '<=', searchTerm + '\uf8ff')
                        .limit(10),
                    productsRef
                        .where('DCI', '>=', searchTerm)
                        .where('DCI', '<=', searchTerm + '\uf8ff')
                        .limit(10)
                ];

                const results = await Promise.allSettled(queries.map(q => q.get()));

                const firestoreProducts = new Set();
                results.forEach(result => {
                    if (result.status === 'fulfilled' && !result.value.empty) {
                        result.value.forEach(doc => {
                            const data = doc.data();
                            firestoreProducts.add({
                                _id: doc.id,
                                nom: data.nom || 'Produit sans nom',
                                DCI: data.DCI || '',
                                forme: data.forme || '',
                                dosage: data.dosage || '',
                                laboratoire: data.laboratoire || data.fabricant || 'Non spécifié',
                                prix: data.prix || '0'
                            });
                        });
                    }
                });

                matchedProducts = Array.from(firestoreProducts).slice(0, limit);

                if (matchedProducts.length > 0) {
                    dispatch({
                        type: SET_AUTOCOMPLETE_PRODUCTS,
                        payload: matchedProducts
                    });
                    return matchedProducts;
                }
            }
        } catch (firestoreError) {
            // Erreur silencieuse, on continue
        }

        dispatch({
            type: SET_AUTOCOMPLETE_PRODUCTS,
            payload: []
        });
        return [];

    } catch (error) {
        dispatch({
            type: SET_AUTOCOMPLETE_PRODUCTS,
            payload: []
        });
        return [];
    }
};

export const getAllProducts = () => async dispatch => {
    return dispatch(fetchProducts());
};

export const fetchAllProducts = () => async dispatch => {
    dispatch(setLoading(true));

    try {
        const currentUser = auth.currentUser || await waitForAuthState();
        if (!currentUser) {
            throw new Error('Vous devez être connecté pour charger la base de médicaments.');
        }

        const headers = {
            Authorization: `Bearer ${await currentUser.getIdToken()}`,
            'Content-Type': 'application/json'
        };
        const idsResponse = await axios.get(`${API_BASE_URL}/allProducts`, {
            headers,
            timeout: 30000
        });
        const productIds = extractProductList(idsResponse.data);
        if (productIds.length === 0) {
            throw new Error('Le serveur n’a renvoyé aucun identifiant de médicament.');
        }

        const hasProductDetails = productIds.some(product =>
            product && typeof product === 'object' &&
            ['nom', 'nomCommercial', 'DCI', 'dci', 'dosage', 'forme', 'laboratoire']
                .some(key => product[key] !== undefined && product[key] !== null)
        );
        let products = hasProductDetails
            ? productIds.map(product => ({
                ...product,
                id: product.id || product._id,
                _id: product._id || product.id,
                source: 'mongodb'
            }))
            : [];
        const allProductIds = Array.from(new Set(productIds
            .map(product => typeof product === 'string' ? product : product && (product._id || product.id))
            .filter(Boolean)
            .map(String)));
        if (allProductIds.length === 0) {
            throw new Error('Le serveur a renvoyé des médicaments sans identifiant exploitable.');
        }

        for (let offset = 0; !hasProductDetails && offset < allProductIds.length; offset += ADMIN_PRODUCTS_BATCH_SIZE) {
            const batchIds = allProductIds.slice(offset, offset + ADMIN_PRODUCTS_BATCH_SIZE);
            if (batchIds.length === 0) continue;

            const response = await axios.post(`${API_BASE_URL}/productsForIds`, {
                ids: batchIds.join(',')
            }, {
                headers,
                timeout: 30000
            });
            const batchProducts = extractProductList(response.data);
            if (batchProducts.length === 0) {
                throw new Error(`Chargement interrompu : aucun détail reçu pour le lot ${Math.floor(offset / ADMIN_PRODUCTS_BATCH_SIZE) + 1}.`);
            }

            batchProducts.forEach(product => {
                const id = product && (product._id || product.id);
                products.push({
                    ...product,
                    ...(id ? { id, _id: id } : {}),
                    source: 'mongodb'
                });
            });
        }

        if (products.length < allProductIds.length) {
            throw new Error(`Chargement incomplet : ${products.length} médicaments reçus sur ${allProductIds.length}.`);
        }

        dispatch({
            type: SET_PRODUCTS,
            payload: products
        });
        dispatch(setMessage({
            message: `${products.length} médicaments récupérés sur ${allProductIds.length}`,
            status: 'success'
        }));
        return products;
    } catch (error) {
        dispatch(setMessage({
            message: error.message || 'Impossible de récupérer tous les médicaments.',
            status: 'danger'
        }));
        throw error;
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchAllMongoProducts = () => async dispatch => {
    dispatch(setLoading(true));

    try {
        const response = await axios.get(`${API_BASE_URL}/products`, {
            params: {
                limit: 5000,
                all: true,
                include: 'substances,interactions,posologies,equivalences,laboratoires'
            },
            timeout: 60000
        });
        const products = extractProductList(response.data);
        if (products.length === 0) {
            throw new Error('MongoDB n’a renvoyé aucun médicament via l’API /products.');
        }

        const mongoProducts = products.map(product => {
            const id = product._id || product.id;
            return {
                ...product,
                ...(id ? { id, _id: id } : {}),
                source: 'mongodb'
            };
        });

        dispatch({
            type: SET_PRODUCTS,
            payload: mongoProducts
        });
        dispatch(setMessage({
            message: `${mongoProducts.length} médicaments récupérés depuis MongoDB`,
            status: 'success'
        }));
        return mongoProducts;
    } catch (error) {
        dispatch(setMessage({
            message: error.message || 'Impossible de récupérer les médicaments depuis MongoDB.',
            status: 'danger'
        }));
        throw error;
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchProductById = (productId) => async (dispatch, getState) => {
    if (!productId) {
        dispatch(setMessage({
            message: 'ID de produit requis',
            status: 'danger'
        }));
        return null;
    }

    dispatch(setLoading(true));

    try {
        // D'abord vérifier dans le state local
        const state = getState();
        const { equivalences, products } = state.products;

        // Chercher dans les équivalences locales
        if (equivalences && equivalences.length > 0) {
            const foundInEquivalences = equivalences.find(p =>
                (p._id && p._id === productId) || (p.id && p.id === productId)
            );
            if (foundInEquivalences) {
                dispatch({
                    type: SET_PRODUCT,
                    payload: foundInEquivalences
                });
                dispatch(setMessage({
                    message: `Produit "${foundInEquivalences.nom}" chargé depuis les équivalences`,
                    status: 'success'
                }));
                return foundInEquivalences;
            }
        }

        // Chercher dans la liste des produits locaux
        if (products && products.length > 0) {
            const foundInProducts = products.find(p =>
                (p._id && p._id === productId) || (p.id && p.id === productId)
            );
            if (foundInProducts) {
                dispatch({
                    type: SET_PRODUCT,
                    payload: foundInProducts
                });
                dispatch(setMessage({
                    message: `Produit "${foundInProducts.nom}" chargé depuis la liste locale`,
                    status: 'success'
                }));
                return foundInProducts;
            }
        }

        let product = null;

        try {
            const API_MONGO_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';
            const response = await axios.get(`${API_MONGO_URL}/products/${productId}`, {
                timeout: 10000
            });

            if (response.data && response.data.product) {
                product = {
                    id: response.data.product._id,
                    _id: response.data.product._id,
                    ...response.data.product,
                    source: 'mongodb'
                };
            } else {
            }
        } catch (mongoError) {
            // Erreur silencieuse, on continue avec les autres sources
        }

        if (!product) {
            try {
                const { auth, firestore } = await import('../../config/firebase');
                const currentUser = auth.currentUser;

                if (currentUser) {
                    const productRef = firestore.collection('products').doc(productId);
                    const doc = await productRef.get();

                    if (doc.exists) {
                        product = { id: doc.id, _id: doc.id, ...doc.data(), source: 'firestore' };
                    } else {
                    }
                } else {
                }
            } catch (firestoreError) {

            }
        }

        if (!product) {
            try {
                const response = await axios.get(`${API_BASE_URL}/products/${productId}`, {
                    timeout: 8000
                });
                product = { ...response.data, source: 'api' };
            } catch (apiError) {

            }
        }

        if (!product) {
            throw new Error('Produit non trouvé');
        }

        dispatch({
            type: SET_PRODUCT,
            payload: product
        });

        dispatch(setMessage({
            message: `Produit "${product.nom}" chargé avec succès`,
            status: 'success'
        }));

        return product;

    } catch (error) {

        dispatch({
            type: SET_PRODUCT,
            payload: null
        });

        dispatch(setMessage({
            message: 'Produit non trouvé',
            status: 'danger'
        }));

        return null;
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchEquivalences = (productId, criterium = 'substance') => async dispatch => {
    if (!productId) {
        dispatch(setMessage({
            message: 'ID de produit requis pour la recherche d\'équivalences',
            status: 'danger'
        }));
        return [];
    }

    dispatch(setLoading(true));

    try {
        const response = await axios.get(`${API_BASE_URL}/products/${productId}/equivalences`, {
            params: { criterium },
            timeout: 10000
        });

        const equivalences = response.data.data || [];

        dispatch({
            type: SET_EQUIVALENCE,
            payload: equivalences
        });

        return equivalences;

    } catch (error) {
        dispatch(setMessage({
            message: 'Impossible de charger les équivalences',
            status: 'danger'
        }));
        return [];
    } finally {
        dispatch(setLoading(false));
    }
};

export const searchProducts = (searchCriteria) => async dispatch => {
    const { query, laboratoire, forme, classe, exact = false } = searchCriteria;

    if (!query && !laboratoire && !forme && !classe) {
        dispatch({
            type: SET_PRODUCTS,
            payload: []
        });
        return [];
    }

    dispatch(setLoading(true));

    try {
        const result = await fetchWithOfflineSupport(
            async () => {
                const response = await axios.get(`${API_BASE_URL}/products/search`, {
                    params: {
                        q: query || '',
                        laboratoire: laboratoire || '',
                        forme: forme || '',
                        classe: classe || '',
                        exact: exact
                    },
                    timeout: 10000
                });
                return response.data.data || [];
            },
            async () => {
                if (query) {
                    return await searchProductsOffline(query, { exact, limit: 100 });
                } else {
                    return await getAllProductsOffline({
                        laboratoire,
                        forme,
                        limit: 100
                    });
                }
            }
        );

        dispatch({
            type: SET_PRODUCTS,
            payload: result.data
        });

        if (result.source === 'offline') {
            dispatch(setMessage({
                message: `${result.data.length} résultats trouvés hors ligne`,
                status: 'info'
            }));
        }

        return result.data;

    } catch (error) {
        dispatch(setMessage({
            message: 'Erreur lors de la recherche',
            status: 'danger'
        }));
        return [];
    } finally {
        dispatch(setLoading(false));
    }
};

export const clearProducts = () => ({
    type: CLEAR_PRODUCTS
});

export const clearAutocomplete = () => ({
    type: SET_AUTOCOMPLETE_PRODUCTS,
    payload: []
});

export const clearSelectedProduct = () => ({
    type: SET_PRODUCT,
    payload: null
});

export const getProducts = (filters = {}, autocomplete = false) => async dispatch => {
    if (autocomplete) {
        const searchTerm = filters.search || filters.nom || filters.DCI || filters.familleTherapeutique || filters.classesPharmacologiques || filters.forme || filters.dosage || '';

        try {
            let realProducts = [];

            if (searchTerm && searchTerm.trim().length >= 1) {
                try {
                    const API_MONGO_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';

                    // Construire les paramètres en fonction du champ recherché
                    const params = { limit: 30 };

                    if (filters.nom) {
                        params.nom = filters.nom.trim();
                    }

                    if (filters.DCI) {
                        // Gérer les DCI avec virgules - rechercher par chaque substance individuellement
                        const dciParts = filters.DCI.split(',').map(part => part.trim()).filter(part => part.length > 0);
                        if (dciParts.length > 0) {
                            // Utiliser le premier composant principal pour la recherche
                            params.dci = dciParts[0];
                        }
                    }

                    if (filters.familleTherapeutique) {
                        params.familletherapeutique = filters.familleTherapeutique.trim();
                    }

                    if (filters.classesPharmacologiques) {
                        params.classesPharmacologiques = filters.classesPharmacologiques.trim();
                    }

                    if (filters.forme) {
                        params.forme = filters.forme.trim();
                    }

                    if (filters.dosage) {
                        params.dosage = filters.dosage.trim();
                    }


                    const response = await axios.get(`${API_MONGO_URL}/products`, {
                        params,
                        timeout: 8000
                    });

                    if (response.data) {
                        // ✅ Support des deux formats : {products: []} ou directement []
                        const rawProducts = response.data.products || response.data;

                        if (Array.isArray(rawProducts) && rawProducts.length > 0) {
                            realProducts = rawProducts.map(p => ({
                                _id: p._id || p.id,
                                nom: p.nom || p.nomCommercial || 'Produit sans nom',
                                nomCommercial: p.nomCommercial || p.nom || '',
                                DCI: p.DCI || p.dci || '',
                                forme: p.forme || p.form || '',
                                dosage: p.dosage || p.dose || '',
                                conditionnement: p.conditionnement || p.packaging || 'Non spécifié',
                                prix: p.prix || p.price || '0',
                                laboratoire: p.laboratoire || p.laboratory || 'Non spécifié',
                                familleTherapeutique: p.familleTherapeutique || '',
                                classesPharmacologiques: p.classesPharmacologiques || '',
                                source: 'mongodb'
                            }));
                        }
                    }
                } catch (mongoError) {
                    dispatch(setMessage({
                        message: 'Erreur lors de la recherche MongoDB: ' + mongoError.message,
                        status: 'warning'
                    }));
                }
            }

            // Si pas de résultats, essayer une recherche plus large
            if (realProducts.length === 0 && filters.nom && filters.nom.trim().length >= 2) {
                try {
                    const API_MONGO_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';

                    const fallbackParams = {
                        limit: 20,
                        search: filters.nom.trim() // Recherche générale par nom
                    };


                    const fallbackResponse = await axios.get(`${API_MONGO_URL}/products`, {
                        params: fallbackParams,
                        timeout: 8000
                    });

                    if (fallbackResponse.data) {
                        const fallbackProducts = fallbackResponse.data.products || fallbackResponse.data;

                        if (Array.isArray(fallbackProducts) && fallbackProducts.length > 0) {
                            realProducts = fallbackProducts.map(p => ({
                                _id: p._id || p.id,
                                nom: p.nom || p.nomCommercial || 'Produit sans nom',
                                nomCommercial: p.nomCommercial || p.nom || '',
                                DCI: p.DCI || p.dci || '',
                                forme: p.forme || p.form || '',
                                dosage: p.dosage || p.dose || '',
                                conditionnement: p.conditionnement || p.packaging || 'Non spécifié',
                                prix: p.prix || p.price || '0',
                                laboratoire: p.laboratoire || p.laboratory || 'Non spécifié',
                                familleTherapeutique: p.familleTherapeutique || '',
                                classesPharmacologiques: p.classesPharmacologiques || '',
                                source: 'mongodb_fallback'
                            }));
                        }
                    }
                } catch (fallbackError) {
                }
            }

            // Filtrage local intelligent basé sur le champ de recherche
            let filteredProducts = realProducts;

            if (searchTerm && searchTerm.length > 0) {
                const term = searchTerm.toLowerCase().trim();

                // Support pour recherche multi-termes (ex: "PARACÉTAMOL CODÉINE")
                const searchWords = term.split(/\s+/).filter(w => w.length > 0);

                // Fonction helper pour vérifier si tous les mots sont présents dans une chaîne
                const containsAllWords = (text, words) => {
                    if (!text) return false;
                    const lowerText = text.toLowerCase();
                    return words.every(word => lowerText.includes(word));
                };

                filteredProducts = realProducts.filter(p => {
                    // Filtre spécifique selon le champ recherché
                    if (filters.nom) {
                        return containsAllWords(p.nom, searchWords) ||
                               containsAllWords(p.nomCommercial, searchWords);
                    }
                    if (filters.DCI) {
                        return containsAllWords(p.DCI, searchWords);
                    }
                    if (filters.familleTherapeutique) {
                        return containsAllWords(p.familleTherapeutique, searchWords);
                    }
                    if (filters.classesPharmacologiques) {
                        return containsAllWords(p.classesPharmacologiques, searchWords);
                    }
                    if (filters.forme) {
                        return containsAllWords(p.forme, searchWords);
                    }
                    if (filters.dosage) {
                        return containsAllWords(p.dosage, searchWords);
                    }

                    // Recherche générique multi-champs si aucun champ spécifique
                    return containsAllWords(p.nom, searchWords) ||
                           containsAllWords(p.nomCommercial, searchWords) ||
                           containsAllWords(p.DCI, searchWords) ||
                           containsAllWords(p.laboratoire, searchWords) ||
                           containsAllWords(p.forme, searchWords);
                });
            }

            // Limiter le nombre de suggestions
            filteredProducts = filteredProducts.slice(0, 20);

            dispatch({
                type: SET_AUTOCOMPLETE_PRODUCTS,
                payload: filteredProducts
            });

            return filteredProducts;

        } catch (error) {
            dispatch({
                type: SET_AUTOCOMPLETE_PRODUCTS,
                payload: []
            });
            return [];
        }
    }

    dispatch(setLoading(true));


    try {
        try {
            const API_MONGO_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';
            const params = { limit: 200 };

            if (filters.nom) {
                const firstWord = filters.nom.trim().split(/\s+/)[0];
                params.nom = firstWord;
            }
            if (filters.DCI) {
                const firstWord = filters.DCI.trim().split(/\s+/)[0];
                params.dci = firstWord;
            }
            if (filters.familleTherapeutique) params.familletherapeutique = filters.familleTherapeutique;
            if (filters.classesPharmacologiques) params.classesPharmacologiques = filters.classesPharmacologiques;
            if (filters.forme) params.forme = filters.forme;
            if (filters.dosage) params.dosage = filters.dosage;


            const response = await axios.get(`${API_MONGO_URL}/products`, {
                params: params,
                timeout: 15000
            });

            if (response.data) {
                const rawProducts = response.data.products || response.data;

                if (Array.isArray(rawProducts) && rawProducts.length > 0) {
                    let mongoProducts = rawProducts.map(product => ({
                        id: product._id || product.id,
                        _id: product._id || product.id,
                        ...product,
                        source: 'mongodb'
                    }));
                if (Object.keys(filters).length > 0) {
                    const containsAllWords = (text, searchText) => {
                        if (!text || !searchText) return false;
                        const lowerText = text.toLowerCase();
                        const words = searchText.toLowerCase().split(/\s+/).filter(w => w.length > 0);
                        return words.every(word => lowerText.includes(word));
                    };

                    mongoProducts = mongoProducts.filter(product => {
                        let matches = true;

                        if (filters.nom) {
                            const nomMatch = (
                                containsAllWords(product.nom, filters.nom) ||
                                containsAllWords(product.nomCommercial, filters.nom) ||
                                containsAllWords(product.fabricant, filters.nom) ||
                                containsAllWords(product.laboratoire, filters.nom)
                            );
                            matches = matches && nomMatch;
                        }

                        if (filters.DCI) {
                            const dciMatch = (
                                containsAllWords(product.DCI, filters.DCI) ||
                                containsAllWords(product.dci, filters.DCI)
                            );
                            matches = matches && dciMatch;
                        }

                        if (filters.familleTherapeutique) {
                            matches = matches && containsAllWords(
                                product.familleTherapeutique,
                                filters.familleTherapeutique
                            );
                        }

                        if (filters.classesPharmacologiques) {
                            matches = matches && containsAllWords(
                                product.classesPharmacologiques || product.classePharmacologique,
                                filters.classesPharmacologiques
                            );
                        }

                        if (filters.forme) {
                            matches = matches && containsAllWords(
                                product.forme,
                                filters.forme
                            );
                        }

                        if (filters.dosage) {
                            matches = matches && containsAllWords(
                                product.dosage,
                                filters.dosage
                            );
                        }

                        return matches;
                    });

                }

                dispatch({
                    type: SET_PRODUCTS,
                    payload: mongoProducts
                });

                dispatch(setMessage({
                    message: mongoProducts.length > 0 ?
                        `${mongoProducts.length} médicament${mongoProducts.length > 1 ? 's' : ''} trouvé${mongoProducts.length > 1 ? 's' : ''}` :
                        'Aucun médicament trouvé pour ces critères',
                    status: mongoProducts.length > 0 ? 'success' : 'warning'
                }));

                dispatch(setLoading(false));
                return mongoProducts;
                }
            }
        } catch (mongoError) {

        }

        try {
            const { auth, firestore } = await import('../../config/firebase');
            const currentUser = auth.currentUser;

            if (currentUser) {
                const productsRef = firestore.collection('products');
                let query = productsRef.limit(200);
                const snapshot = await query.get();

                if (snapshot.size > 0) {
                    const allProducts = [];
                    snapshot.forEach(doc => {
                        const data = doc.data();
                        allProducts.push({
                            id: doc.id,
                            ...data
                        });
                    });

                    let filteredProducts = allProducts;
                    if (Object.keys(filters).length > 0) {
                        const containsAllWords = (text, searchText) => {
                            if (!text || !searchText) return false;
                            const lowerText = text.toLowerCase();
                            const words = searchText.toLowerCase().split(/\s+/).filter(w => w.length > 0);
                            return words.every(word => lowerText.includes(word));
                        };

                        filteredProducts = allProducts.filter(product => {
                            let matches = true;

                            if (filters.nom) {
                                const searchTerm = filters.nom;
                                const nomMatch = (
                                    containsAllWords(product.nom, searchTerm) ||
                                    containsAllWords(product.nomCommercial, searchTerm) ||
                                    containsAllWords(product.fabricant, searchTerm) ||
                                    containsAllWords(product.laboratoire, searchTerm)
                                );
                                matches = matches && nomMatch;
                            }

                            if (filters.DCI) {
                                const dciMatch = (
                                    containsAllWords(product.DCI, filters.DCI) ||
                                    containsAllWords(product.dci, filters.DCI)
                                );
                                matches = matches && dciMatch;
                            }

                            if (filters.familleTherapeutique) {
                                const familleMatch = containsAllWords(
                                    product.familleTherapeutique,
                                    filters.familleTherapeutique
                                );
                                matches = matches && familleMatch;
                            }

                            return matches;
                        });
                    }
                    if (filteredProducts.length > 0) {
                        dispatch({
                            type: SET_PRODUCTS,
                            payload: filteredProducts
                        });

                        dispatch(setMessage({
                            message: `${filteredProducts.length} médicaments trouvés`,
                            status: 'success'
                        }));

                        dispatch(setLoading(false));
                        return filteredProducts;
                    } else {
                    }
                }
            }
        } catch (firestoreError) {
            // Erreur silencieuse, on continue
        }

        try {
            const params = {};
            if (filters.nom) params.nom = filters.nom;
            if (filters.DCI) params.DCI = filters.DCI;
            if (filters.familleTherapeutique) params.familleTherapeutique = filters.familleTherapeutique;
            if (filters.classesPharmacologiques) params.classesPharmacologiques = filters.classesPharmacologiques;
            if (filters.forme) params.forme = filters.forme;
            if (filters.dosage) params.dosage = filters.dosage;

            const response = await axios.get(`${API_BASE_URL}/products`, {
                params,
                timeout: 10000
            });

            const products = response.data.products || [];

            if (products.length > 0) {
                dispatch({
                    type: SET_PRODUCTS,
                    payload: products
                });

                dispatch(setMessage({
                    message: `${products.length} médicaments trouvés`,
                    status: 'success'
                }));

                dispatch(setLoading(false));
                return products;
            } else {
            }

        } catch (apiError) {
            // Erreur silencieuse, on continue
        }

        try {
            const { getAllProductsOffline } = await import('../../helpers/offlineApi');

            let products = await getAllProductsOffline({
                limit: 1000
            });

            if (Object.keys(filters).length > 0) {
                const containsAllWords = (text, searchText) => {
                    if (!text || !searchText) return false;
                    const lowerText = text.toLowerCase();
                    const words = searchText.toLowerCase().split(/\s+/).filter(w => w.length > 0);
                    return words.every(word => lowerText.includes(word));
                };

                products = products.filter(product => {
                    let matches = true;

                    if (filters.nom) {
                        const nomMatch = (
                            containsAllWords(product.nom, filters.nom) ||
                            containsAllWords(product.nomCommercial, filters.nom) ||
                            containsAllWords(product.fabricant, filters.nom) ||
                            containsAllWords(product.laboratoire, filters.nom)
                        );
                        matches = matches && nomMatch;
                    }

                    if (filters.DCI) {
                        const dciMatch = (
                            containsAllWords(product.DCI, filters.DCI) ||
                            containsAllWords(product.dci, filters.DCI)
                        );
                        matches = matches && dciMatch;
                    }

                    if (filters.familleTherapeutique) {
                        matches = matches && containsAllWords(
                            product.familleTherapeutique,
                            filters.familleTherapeutique
                        );
                    }

                    if (filters.classesPharmacologiques) {
                        matches = matches && containsAllWords(
                            product.classesPharmacologiques || product.classePharmacologique,
                            filters.classesPharmacologiques
                        );
                    }

                    if (filters.forme) {
                        matches = matches && containsAllWords(
                            product.forme,
                            filters.forme
                        );
                    }

                    if (filters.dosage) {
                        matches = matches && containsAllWords(
                            product.dosage,
                            filters.dosage
                        );
                    }

                    return matches;
                });
            }

            dispatch({
                type: SET_PRODUCTS,
                payload: products
            });

            dispatch(setMessage({
                message: products.length > 0
                    ? `${products.length} médicament${products.length > 1 ? 's' : ''} trouvé${products.length > 1 ? 's' : ''} en cache local`
                    : 'Aucun médicament trouvé pour ces critères',
                status: products.length > 0 ? 'info' : 'warning'
            }));

            dispatch(setLoading(false));
            return products;

        } catch (cacheError) {
            // Erreur silencieuse, on continue
        }

        dispatch({
            type: SET_PRODUCTS,
            payload: []
        });

        dispatch(setMessage({
            message: 'Aucun produit trouvé. Vérifiez votre connexion.',
            status: 'warning'
        }));

        dispatch(setLoading(false));
        return [];

    } catch (error) {
        dispatch(setMessage({
            message: `Erreur lors de la recherche: ${error.message}`,
            status: 'danger'
        }));
        dispatch(setLoading(false));
        return [];
    }
};

export const setAutocompleteProducts = (products) => ({
    type: SET_AUTOCOMPLETE_PRODUCTS,
    payload: products
});

export const removeProductFromList = (productId) => ({
    type: 'REMOVE_PRODUCT_FROM_LIST',
    payload: productId
});

export const setProduct = (product) => ({
    type: SET_PRODUCT,
    payload: product
});

export const getFilterProducts = (product = {}) => async dispatch => {
    if (!product || Object.keys(product).length === 0) {
        dispatch({
            type: SET_EQUIVALENCE,
            payload: []
        });
        return [];
    }

    dispatch(setLoading(true));

    try {
        const requiredDCI = product.DCI || product.dci;
        const requiredForme = product.forme;
        const requiredDosage = product.dosage;

        if (!requiredDCI) {
            dispatch({
                type: SET_EQUIVALENCE,
                payload: []
            });

            dispatch(setMessage({
                message: 'Impossible de trouver des équivalents: DCI manquante',
                status: 'warning'
            }));

            return [];
        }

        let equivalentProducts = [];

        try {
            const searchStrategies = [

                {
                    params: {
                        limit: 200,
                        dci: requiredDCI
                    },
                    name: 'DCI'
                },

                {
                    params: {
                        limit: 200,
                        nom: requiredDCI
                    },
                    name: 'nom-DCI'
                },

                {
                    params: {
                        limit: 200,
                        dci: requiredDCI,
                        forme: requiredForme
                    },
                    name: 'DCI-forme'
                },

                {
                    params: {
                        limit: 200,
                        substance: requiredDCI
                    },
                    name: 'substance'
                }
            ];

            // Exécuter toutes les stratégies de recherche
            const searchPromises = searchStrategies.map(async (strategy) => {
                try {

                    const response = await axios.get(`${API_BASE_URL}/products`, {
                        params: strategy.params,
                        timeout: 15000
                    });

                    let products = [];


                    if (Array.isArray(response.data)) {
                        products = response.data;
                    } else if (response.data && response.data.products && Array.isArray(response.data.products)) {
                        products = response.data.products;
                    }

                    return products;
                } catch (error) {
                    return [];
                }
            });

            // Attendre toutes les recherches
            const allResults = await Promise.all(searchPromises);
            const allProducts = allResults.flat();


            if (allProducts.length > 0) {
                equivalentProducts = allProducts
                    .filter(p => {
                        // Exclure le produit lui-même
                        const isNotSelf = p._id !== product._id && p.id !== product._id;

                        if (!isNotSelf) return false;

                        const productDCI = (p.DCI || p.dci || p.substance || '').toLowerCase().trim();
                        const productForme = (p.forme || '').toLowerCase().trim();
                        const productDosage = (p.dosage || '').toLowerCase().trim();

                        // Vérification DCI (obligatoire)
                        const sameDCI = productDCI === requiredDCI.toLowerCase().trim() ||
                                      productDCI.includes(requiredDCI.toLowerCase().trim()) ||
                                      requiredDCI.toLowerCase().trim().includes(productDCI);

                        if (!sameDCI) return false;


                        // Tout médicament avec même DCI est considéré comme équivalent
                        return sameDCI;
                    })
                    .map(p => {
                        const productDCI = (p.DCI || p.dci || p.substance || '').toLowerCase().trim();
                        const productForme = (p.forme || '').toLowerCase().trim();
                        const productDosage = (p.dosage || '').toLowerCase().trim();

                        let score = 0;
                        // Score de base pour DCI (le plus important)
                        if (productDCI === requiredDCI.toLowerCase().trim()) score += 150;
                        else if (productDCI.includes(requiredDCI.toLowerCase().trim())) score += 120;
                        else if (requiredDCI.toLowerCase().trim().includes(productDCI)) score += 100;

                        if (requiredForme && productForme) {
                            if (productForme === requiredForme.toLowerCase().trim()) score += 50;
                            else if (productForme.includes(requiredForme.toLowerCase().trim()) ||
                                     requiredForme.toLowerCase().trim().includes(productForme)) score += 25;
                        }

                        if (requiredDosage && productDosage) {
                            if (productDosage === requiredDosage.toLowerCase().trim()) score += 50;
                            else if (productDosage.includes(requiredDosage.toLowerCase().trim()) ||
                                     requiredDosage.toLowerCase().trim().includes(productDosage)) score += 25;
                        }

                        return {
                            _id: p._id,
                            nom: p.nom || p.nomCommercial || 'Produit sans nom',
                            DCI: p.DCI || p.dci || p.substance || '',
                            forme: p.forme || '',
                            dosage: p.dosage || '',
                            fabricant: p.fabricant || p.laboratoire || 'Non spécifié',
                            prix: p.prix || 0,
                            conditionnement: p.conditionnement || '',
                            familleTherapeutique: p.familleTherapeutique || '',
                            classesPharmacologiques: p.classesPharmacologiques || '',
                            source: 'mongodb_equivalence',
                            equivalenceScore: score
                        };
                    });
            }

        } catch (apiError) {
            dispatch(setMessage({
                message: 'Erreur lors de la recherche d\'équivalents via API',
                status: 'warning'
            }));
        }

        // Éliminer les doublons par _id et trier par score
        const uniqueProducts = [];
        const seenIds = new Set();

        for (const prod of equivalentProducts) {
            if (!seenIds.has(prod._id)) {
                uniqueProducts.push(prod);
                seenIds.add(prod._id);
            }
        }

        // Trier par score décroissant
        const sortedProducts = uniqueProducts
            .sort((a, b) => (b.equivalenceScore || 0) - (a.equivalenceScore || 0));

        // Garder plus de résultats (100 au lieu de 25)
        const finalProducts = sortedProducts.slice(0, 100);


        dispatch({
            type: SET_EQUIVALENCE,
            payload: finalProducts
        });

        if (finalProducts.length > 0) {
            const strictEquivalents = finalProducts.filter(p => p.equivalenceScore >= 200);
            const partialEquivalents = finalProducts.length - strictEquivalents.length;

            let message = `${finalProducts.length} équivalent(s) trouvé(s)`;
            if (strictEquivalents.length > 0 && partialEquivalents > 0) {
                message += ` (${strictEquivalents.length} exacts, ${partialEquivalents} partiels)`;
            }

            dispatch(setMessage({
                message: message,
                status: 'success'
            }));
        } else {
            dispatch(setMessage({
                message: 'Aucun équivalent trouvé. Essayez avec des critères différents.',
                status: 'info'
            }));
        }

        return finalProducts;

    } catch (error) {
        dispatch(setMessage({
            message: 'Impossible de rechercher les équivalents: ' + (error.message || 'Erreur inconnue'),
            status: 'danger'
        }));

        dispatch({
            type: SET_EQUIVALENCE,
            payload: []
        });

        return [];
    } finally {
        dispatch(setLoading(false));
    }
};

export const getProductById = (productId) => async dispatch => {
    return dispatch(fetchProductById(productId));
};

export const newProduct = (productData) => async dispatch => {
    if (!productData.nom) {
        const errorMessage = 'Nom du produit requis';
        dispatch(setMessage({
            message: errorMessage,
            status: 'danger'
        }));
        window.alert(`Erreur : ${errorMessage}`);
        return null;
    }

    dispatch(setLoading(true));

    try {
        const response = await axios.post(`${API_BASE_URL}/products`, productData, {
            timeout: 10000
        });

        const newProduct = response.data;
        const successMessage = 'Produit créé avec succès';

        dispatch(setMessage({
            message: successMessage,
            status: 'success'
        }));

        // Afficher une boîte de dialogue de succès
        window.alert(`✅ ${successMessage}\n\nNom du produit : ${productData.nom}`);

        dispatch(fetchProducts());

        return newProduct;

    } catch (error) {
        const errorMessage = error.response?.data?.message || 'Erreur lors de la création du produit';
        dispatch(setMessage({
            message: errorMessage,
            status: 'danger'
        }));

        // Afficher une boîte de dialogue d'erreur
        window.alert(`❌ Erreur : ${errorMessage}\nVeuillez réessayer.`);

        return null;
    } finally {
        dispatch(setLoading(false));
    }
};

export const editProduct = (productId, updateData) => async dispatch => {
    if (!productId) {
        dispatch(setMessage({
            message: 'ID de produit requis',
            status: 'danger'
        }));
        dispatch(setLoading(false));
        return false;
    }

    dispatch(setLoading(true));

    try {
        const API_MONGO_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';
        const requestData = {
            product: {
                ...updateData,
                _id: productId
            }
        };
        const response = await axios.post(
            `${API_MONGO_URL}/products/${productId}`,
            requestData,
            {
                timeout: 30000,  // Timeout plus long
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );

        if (response.data && response.data.product) {
            const message = 'Produit modifié avec succès';
            dispatch(setMessage({
                message,
                status: 'success'
            }));

            // Mettre à jour le produit dans le store
            dispatch({
                type: SET_PRODUCT,
                payload: response.data.product
            });

            return response.data.product;
        } else {
            throw new Error('Réponse serveur invalide');
        }

    } catch (error) {

        let userMessage = 'Erreur lors de la modification du produit';
        let shouldTryFallback = false;

        if (error.response) {
            if (error.response.status === 500) {
                // Erreur 500 - problème serveur
                userMessage = 'Échec lors de l\'enregistrement du produit. Le serveur rencontre un problème technique.';
                shouldTryFallback = true;

            } else if (error.response.status === 404) {
                userMessage = 'Le serveur n\'a pas trouvé la route. Contactez l\'administrateur.';
            } else if (error.response.status === 400) {
                userMessage = 'Données invalides envoyées au serveur. Vérifiez les champs obligatoires.';
            } else if (error.response.data?.error?.message) {
                userMessage = error.response.data.error.message;
            } else if (error.response.data?.message) {
                userMessage = error.response.data.message;
            } else {
                userMessage = `Erreur serveur (${error.response.status}). Veuillez réessayer.`;
            }
        } else if (error.code === 'ECONNABORTED') {
            userMessage = 'La requête a expiré. Vérifiez votre connexion internet et réessayez.';
            shouldTryFallback = true;
        } else if (error.message.includes('Network Error')) {
            userMessage = 'Erreur de connexion. Vérifiez votre connexion internet.';
            shouldTryFallback = true;
        }

        // Tentative de sauvegarde locale en cas d'erreur critique
        if (shouldTryFallback) {
            try {


                // Sauvegarder localement dans le store Redux
                const updatedProduct = {
                    ...updateData,
                    _id: productId,
                    id: productId,
                    lastModified: new Date().toISOString(),
                    pendingSync: true
                };

                dispatch({
                    type: SET_PRODUCT,
                    payload: updatedProduct
                });

                // Sauvegarder dans le localStorage pour persistance
                const localProducts = JSON.parse(localStorage.getItem('pendingProductUpdates') || '[]');
                const existingIndex = localProducts.findIndex(p => p._id === productId);

                if (existingIndex >= 0) {
                    localProducts[existingIndex] = updatedProduct;
                } else {
                    localProducts.push(updatedProduct);
                }

                localStorage.setItem('pendingProductUpdates', JSON.stringify(localProducts));

                dispatch(setMessage({
                    message: `${userMessage} Modifications sauvegardées localement. Elles seront synchronisées quand le serveur sera disponible.`,
                    status: 'warning'
                }));

                return updatedProduct;

            } catch (fallbackError) {
                dispatch(setMessage({
                    message: userMessage + ' Impossible de sauvegarder localement.',
                    status: 'danger'
                }));
            }
        } else {
            dispatch(setMessage({
                message: userMessage,
                status: 'danger'
            }));
        }

        return null;
    } finally {
        dispatch(setLoading(false));
    }
};

// Fonction pour synchroniser les modifications en attente
export const syncPendingProductUpdates = () => async dispatch => {
    const pendingUpdates = JSON.parse(localStorage.getItem('pendingProductUpdates') || '[]');

    if (pendingUpdates.length === 0) {
        return;
    }


    const syncResults = {
        success: 0,
        failed: 0,
        errors: []
    };

    for (const product of pendingUpdates) {
        try {
            const { pendingSync, lastModified, ...productData } = product;

            // Tenter de synchroniser chaque produit
            const result = await dispatch(editProduct(product._id, productData));

            if (result) {
                syncResults.success++;
            } else {
                syncResults.failed++;
                syncResults.errors.push(`Échec: ${product.nom || product._id}`);
            }
        } catch (error) {
            syncResults.failed++;
            syncResults.errors.push(`Erreur: ${product.nom || product._id} - ${error.message}`);
        }
    }

    // Nettoyer les éléments synchronisés avec succès
    if (syncResults.success > 0) {
        const remainingUpdates = pendingUpdates.filter((_, index) =>
            index >= syncResults.success
        );
        localStorage.setItem('pendingProductUpdates', JSON.stringify(remainingUpdates));
    }

    // Informer l'utilisateur du résultat
    if (syncResults.success > 0) {
        dispatch(setMessage({
            message: `Synchronisation réussie: ${syncResults.success} produit(s) mis à jour.`,
            status: 'success'
        }));
    }

    if (syncResults.failed > 0) {
        dispatch(setMessage({
            message: `Attention: ${syncResults.failed} produit(s) n'ont pas pu être synchronisés.`,
            status: 'warning'
        }));
    }

    return syncResults;
};

// Vérifier s'il y a des mises à jour en attente
export const checkPendingUpdates = () => dispatch => {
    const pendingUpdates = JSON.parse(localStorage.getItem('pendingProductUpdates') || '[]');

    if (pendingUpdates.length > 0) {
        dispatch(setMessage({
            message: `Vous avez ${pendingUpdates.length} modification(s) en attente de synchronisation.`,
            status: 'info'
        }));

        // Proposer une synchronisation automatique
        setTimeout(() => {
            dispatch(syncPendingProductUpdates());
        }, 2000);
    }
};

export const softDeleteProduct = (productId) => async dispatch => {
    dispatch(setLoading(true));

    try {
        // Mettre à jour le statut du produit au lieu de le supprimer
        const response = await axios.put(`${API_BASE_URL}/products/${productId}`, {
            statutMedicament: 'médicament retiré du marché',
            isActive: false,
            dateRetrait: new Date().toISOString()
        }, {
            timeout: 15000
        });

        if (response.data && response.data.success) {
            dispatch(setMessage({
                message: 'Produit retiré avec succès',
                status: 'success'
            }));

            // Rafraîchir la liste des produits pour refléter le changement
            dispatch(fetchProducts());
            return true;
        } else {
            throw new Error('Erreur lors du retrait du produit');
        }

    } catch (error) {
        dispatch(setMessage({
            message: 'Erreur lors du retrait du produit',
            status: 'danger'
        }));
        return false;
    } finally {
        dispatch(setLoading(false));
    }
};

export default {
    fetchProducts,
    getAllProducts,
    getProducts,
    getFilterProducts,
    getProductById,
    fetchAutocompleteProducts,
    setAutocompleteProducts,
    setProduct,
    newProduct,
    fetchProductById,
    fetchEquivalences,
    searchProducts,
    editProduct,
    clearProducts,
    clearAutocomplete,
    clearSelectedProduct,
    softDeleteProduct,
    syncPendingProductUpdates,
    checkPendingUpdates
};
